import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, Paper, Button } from '@mui/material';
import { getReviewsData } from '../../../utils/reviewsAdapter';
import AspectSentimentMatrix from './AspectSentimentMatrix';
import UserJourney from './UserJourney';
import RequestedFeatures from './RequestedFeatures';
// import DelightFeatures from './DelightFeatures';
// import NegativeIssues from './NegativeIssues';
import FeatureIssueMatrix from './FeatureIssueMatrix';
import CompetitiveAnalysis from './CompetitiveAnalysis';
import ThematicClusters from './ThematicClusters';
import EmotionalAnalysis from './EmotionalAnalysis';
import OverallSummary from './OverallSummary';
import TimeBasedReviewChart from './TimeBasedReviewChart';
import FreeBlockModal from 'sections/premiumBlock/FreeBlockModal';
import { openModal } from 'store/reducers/actions';
import { motion } from 'framer-motion';
import useAntiTamperBlur from 'hooks/useAntiTamperBlur';

/**
 * Container component for the V2 version of reviews analysis
 * This component decides which v2 subcomponents to render based on the data
 */
const ReviewsV2Container = ({ exampleReport = false }) => {
  // Get the reviews data using our adapter utility
  const reviewsState = useSelector((state) => state.reviewsReducer);
  const data = getReviewsData(reviewsState);
  console.log(reviewsState);

  // Hooks for external highlighting (must be before any early return)
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const clearExternalHighlights = () => {
      if (!containerRef.current) return;
      const nodes = containerRef.current.querySelectorAll('.external-citation-highlight');
      nodes.forEach((node) => {
        const parent = node.parentNode;
        if (!parent) return;
        parent.replaceChild(document.createTextNode(node.textContent), node);
        parent.normalize();
      });
    };

    const applyExternalHighlight = (text) => {
      if (!containerRef.current || !text) return false;
      const base = String(text).trim();
      if (!base) return false;
      const ensureEllipsis = (s) => (/[.…]$/.test(s) || /\.\.\.$/.test(s) ? s : `${s}…`);
      const swapDots = (s) => s.replace(/\.\.\./g, '…');
      const swapEllipsis = (s) => s.replace(/…/g, '...');
      const stripQuotes = (s) => s.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
      const variants = Array.from(
        new Set([
          base,
          ensureEllipsis(base),
          swapDots(base),
          swapDots(ensureEllipsis(base)),
          swapEllipsis(base),
          stripQuotes(base),
          stripQuotes(ensureEllipsis(base))
        ])
      );
      const walker = document.createTreeWalker(containerRef.current, NodeFilter.SHOW_TEXT, null, false);
      let node;
      while ((node = walker.nextNode())) {
        const content = node.textContent || '';
        const contentLower = content.toLowerCase();
        let foundStart = -1;
        let foundLen = 0;
        for (const v of variants) {
          const vLower = String(v).toLowerCase();
          const i = contentLower.indexOf(vLower);
          if (i !== -1) {
            foundStart = i;
            foundLen = vLower.length;
            break;
          }
        }
        if (foundStart !== -1) {
          try {
            const range = document.createRange();
            range.setStart(node, foundStart);
            range.setEnd(node, foundStart + foundLen);
            const span = document.createElement('span');
            span.className = 'external-citation-highlight';
            span.style.backgroundColor = 'rgba(255, 215, 0, 0.35)';
            span.style.borderRadius = '2px';
            span.style.padding = '1px 2px';
            try {
              range.surroundContents(span);
            } catch (e) {
              span.appendChild(range.extractContents());
              range.insertNode(span);
            }
            setTimeout(() => {
              try {
                span.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } catch (_) {
                /* ignore */
              }
            }, 0);
            return true;
          } catch (e) {
            // continue
          }
        }
      }
      return false;
    };

    const onHighlight = (evt) => {
      const text = evt && evt.detail ? evt.detail.text : '';
      if (!text) return;
      clearExternalHighlights();
      if (!applyExternalHighlight(text)) {
        const stripped = String(text)
          .replace(/["'“”]/g, '')
          .trim();
        if (stripped) applyExternalHighlight(stripped);
      }
    };
    const onClear = () => clearExternalHighlights();
    window.addEventListener('reviews-analysis:highlight', onHighlight);
    window.addEventListener('reviews-analysis:clear-highlight', onClear);
    return () => {
      window.removeEventListener('reviews-analysis:highlight', onHighlight);
      window.removeEventListener('reviews-analysis:clear-highlight', onClear);
      clearExternalHighlights();
    };
  }, []);
  const plan = useSelector((state) => state.proAccountReducer.plan);
  const trialWasActivated = useSelector((state) => state.proAccountReducer?.trial_was_activated);
  const userCountry = useSelector((state) => state.proAccountReducer?.country);
  const restrictedCountries = ['IN', 'VN', 'CN', 'BD', 'PK', 'ID'];
  const trialAllowed = !trialWasActivated && !restrictedCountries.includes(userCountry);
  const isProUser = plan === 'Trial' || plan === 'Pro' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';
  const isModalOpen = useSelector((state) => state.modalReducer.isOpen);
  const dispatch = useDispatch();
  const handleTrialClick = () => {
    if (isProUser) return;
    dispatch(
      openModal({
        title: 'Get access to full Reviews Analysis',
        subtitle: 'Upgrade to Pro to view all insights from Reviews Analysis.',
        trial: trialAllowed
      })
    );
  };

  const Gate = ({ children, cta }) => {
    const contentRef = React.useRef(null);
    const overlayRef = React.useRef(null);
    useAntiTamperBlur(contentRef, overlayRef, { blurPx: 5 });
    if (isProUser) return children;
    return (
      <Box sx={{ position: 'relative' }}>
        <Box ref={contentRef} sx={{ filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.85 }}>
          {children}
        </Box>
        <Paper
          ref={overlayRef}
          elevation={3}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 3,
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(255,255,255,0.6)'
          }}
        >
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.35 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 6,
                p: { xs: 2, md: 3 },
                width: '92%',
                maxWidth: 720,
                textAlign: 'left'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {cta?.title || 'Unlock full report'}
              </Typography>
              {Array.isArray(cta?.bullets) && cta.bullets.length > 0 && (
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {cta.bullets.map((b, i) => (
                    <Grid key={i} item xs={12} sm={6}>
                      <Typography variant="body2">• {b}</Typography>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleTrialClick}
                  component={motion.button}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    px: 3,
                    py: 1.25,
                    background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)',
                    boxShadow: 'none'
                  }}
                >
                  {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Cancel anytime
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Paper>
      </Box>
    );
  };

  if (!data || !data.review_analysis_results) {
    return (
      <Box sx={{ p: 3 }} ref={containerRef}>
        <Typography variant="h5">No reviews analysis data available</Typography>
      </Box>
    );
  }

  // Extract the different sections of v2 data
  const { negative_issues, product_aspects, delight_features, feature_requests, thematic_clusters, competitive_analysis } =
    data.review_analysis_results;

  // Convert product_aspects to format needed by AspectSentimentMatrix
  const prepareAspectData = () => {
    if (!product_aspects) return [];

    return Object.entries(product_aspects).map(([aspect, details]) => {
      return {
        aspect,
        sentiment_score: details.sentiment_score || 0,
        business_priority: details.business_priority || 'medium',
        mentions_count: details.mentions_count || 0,
        key_insight: details.key_insights
      };
    });
  };

  const aspectSentimentData = prepareAspectData();

  return (
    <Box sx={{ p: 2 }} ref={containerRef}>
      {!isProUser && isModalOpen ? <FreeBlockModal /> : null}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h3" gutterBottom>
            Reviews Analysis for {data.url}
          </Typography>
        </Grid>

        {/* Overall Summary Section */}
        <Grid item xs={12}>
          <OverallSummary />
        </Grid>

        {/* Time-Based Review Chart */}
        <Grid item xs={12}>
          <TimeBasedReviewChart exampleReport={exampleReport} />
        </Grid>

        {/* Aspect Sentiment Matrix (paywalled) */}
        {product_aspects && (
          <Grid item xs={12}>
            {!isProUser && (
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Aspect Sentiment
              </Typography>
            )}
            <Gate
              cta={{
                title: 'Unlock Aspect Sentiment Matrix',
                bullets: ['Full aspect list', 'Sentiment score & mentions', 'Business priority', 'Key insights per aspect']
              }}
            >
              <AspectSentimentMatrix aspectData={aspectSentimentData} />
            </Gate>
          </Grid>
        )}

        {/* User Journey (paywalled) */}
        <Grid item xs={12}>
          {!isProUser && (
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              User Journey
            </Typography>
          )}
          <Gate
            cta={{
              title: 'Unlock User Journey Insights',
              bullets: ['Stages & transitions', 'Drop-offs & friction', 'Opportunities across the journey']
            }}
          >
            <UserJourney />
          </Gate>
        </Grid>

        {/* Emotional Analysis (paywalled) */}
        {data.review_analysis_results.emotional_analysis && (
          <>
            {!isProUser && (
              <Typography variant="h4" sx={{ fontWeight: 700, m: 1, width: '100%' }}>
                Emotional Analysis
              </Typography>
            )}
            <Gate
              cta={{
                title: 'Unlock Emotional Analysis',
                bullets: ['All emotion categories', 'Intensity & trends over time']
              }}
            >
              <EmotionalAnalysis />
            </Gate>
          </>
        )}

        {/* Feature Requests (paywalled) */}
        {feature_requests && (
          <Grid item xs={12}>
            {!isProUser && (
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Most Requested Features
              </Typography>
            )}
            <Gate
              cta={{
                title: 'Unlock Requested Features',
                bullets: ['Full requests list', 'Workarounds & quotes', 'Urgency & business value']
              }}
            >
              <RequestedFeatures />
            </Gate>
          </Grid>
        )}

        {/* Delight Features and Negative Issues combined matrix (paywalled) */}
        {(delight_features || negative_issues) && (
          <Grid item xs={12}>
            {!isProUser && (
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Feature vs Issue Matrix
              </Typography>
            )}
            <Gate
              cta={{
                title: 'Unlock Feature vs Issue Matrix',
                bullets: ['Delight vs pain points', 'Priority × impact matrix', 'Actionable insights']
              }}
            >
              <FeatureIssueMatrix />
            </Gate>
          </Grid>
        )}

        {/* Competitive Analysis (paywalled) */}
        {competitive_analysis &&
          !(
            Object.keys(competitive_analysis).length === 1 &&
            competitive_analysis.None &&
            Object.keys(competitive_analysis.None).length === 0
          ) && (
            <Grid item xs={12}>
              {!isProUser && (
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Competitive Analysis
                </Typography>
              )}
              <Gate
                cta={{
                  title: 'Unlock Competitive Benchmarks',
                  bullets: ['Competitor matrix', 'Strengths & gaps', 'Positioning insights']
                }}
              >
                <CompetitiveAnalysis />
              </Gate>
            </Grid>
          )}

        {/* Thematic Clusters (paywalled) */}
        {thematic_clusters && (
          <Grid item xs={12}>
            {!isProUser && (
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Thematic Clusters
              </Typography>
            )}
            <Gate
              cta={{
                title: 'Unlock Thematic Clusters',
                bullets: ['All clusters', 'Representative quotes', 'Emerging themes']
              }}
            >
              <ThematicClusters />
            </Gate>
          </Grid>
        )}

        {/* Placeholder for other V2 components */}
      </Grid>
    </Box>
  );
};

export default ReviewsV2Container;
